import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { CabecalhoDePagina } from '@/components/CabecalhoDePagina';
import { IconeAlerta, IconeExcluir } from '@/components/Icones';
import { CatalogoDeCamadas } from '@/features/admin-ingestao/CatalogoDeCamadas';
import { FormularioDeIngestao } from '@/features/admin-ingestao/FormularioDeIngestao';
import { HistoricoDeIngestoes } from '@/features/admin-ingestao/HistoricoDeIngestoes';
import { Modal } from '@/features/admin-ingestao/Modal';
import { consultarSituacaoDaIngestao, excluirIngestao, listarCamadasAdmin, listarHistoricoDeIngestoes, } from '@/features/admin-ingestao/requestsAdmin';
import { destacar, renderizarComDestaque, rotuloDoStatus } from '@/features/admin-ingestao/apoioDeTexto';
import { useSessaoAdmin } from '@/features/autenticacao-admin/sessaoAdmin';
import { useI18n } from '@/i18n/I18nProvider';
import { ErroDaGeoEngine } from '@/services/geo-engine/errors';
const INTERVALO_DE_POLLING_MS = 3000;
const LIMITE_DO_HISTORICO = 20;
/**
 * Página de Ingestão: histórico de execuções, catálogo de camadas publicadas, submissão de
 * novas ingestões e acompanhamento da execução selecionada. Toda chamada usa exclusivamente
 * a sessão administrativa — nenhuma delas envia o Bearer técnico nem o JWT administrativo
 * como se fosse ele.
 */
export function PaginaIngestao() {
    const { t, formatar, traduzirErro } = useI18n();
    const { sessao, sair } = useSessaoAdmin();
    const token = sessao === null || sessao === undefined ? null : sessao.token;
    const [historico, setHistorico] = useState(null);
    const [carregandoHistorico, setCarregandoHistorico] = useState(false);
    const [erroHistorico, setErroHistorico] = useState(null);
    const [deslocamentoHistorico, setDeslocamentoHistorico] = useState(0);
    const [camadas, setCamadas] = useState(null);
    const [carregandoCamadas, setCarregandoCamadas] = useState(false);
    const [erroCamadas, setErroCamadas] = useState(null);
    const [ingestionSelecionada, setIngestionSelecionada] = useState(null);
    const [situacao, setSituacao] = useState(null);
    const [consultandoSituacao, setConsultandoSituacao] = useState(false);
    const [erroDeConsulta, setErroDeConsulta] = useState(null);
    const [formularioAberto, setFormularioAberto] = useState(false);
    const [excluindoId, setExcluindoId] = useState(null);
    const [confirmacaoDeExclusao, setConfirmacaoDeExclusao] = useState(null);
    const [erroDeExclusao, setErroDeExclusao] = useState(null);
    const [erroDeAtualizacao, setErroDeAtualizacao] = useState(null);
    const textoDaFalha = (falha, textoSeDesconhecida) => falha === 'desconhecida' ? textoSeDesconhecida : traduzirErro(falha);
    const contagem = (valor) => valor === null || valor === undefined ? '—' : formatar.inteiro(valor);
    const carregarHistorico = useCallback(async (sinal) => {
        if (token === null) {
            return;
        }
        setCarregandoHistorico(true);
        setErroHistorico(null);
        try {
            const { dados } = await listarHistoricoDeIngestoes(token, { limit: LIMITE_DO_HISTORICO, offset: deslocamentoHistorico }, sinal);
            setHistorico(dados);
        }
        catch (falha) {
            if (falha instanceof ErroDaGeoEngine) {
                setErroHistorico(falha);
            }
            else if (!(falha instanceof DOMException && falha.name === 'AbortError')) {
                throw falha;
            }
        }
        finally {
            setCarregandoHistorico(false);
        }
    }, [token, deslocamentoHistorico]);
    const carregarCamadas = useCallback(async (sinal) => {
        if (token === null) {
            return;
        }
        setCarregandoCamadas(true);
        setErroCamadas(null);
        try {
            const { dados } = await listarCamadasAdmin(token, sinal);
            setCamadas(dados);
        }
        catch (falha) {
            if (falha instanceof ErroDaGeoEngine) {
                setErroCamadas(falha);
            }
            else if (!(falha instanceof DOMException && falha.name === 'AbortError')) {
                throw falha;
            }
        }
        finally {
            setCarregandoCamadas(false);
        }
    }, [token]);
    const irParaPaginaAnterior = useCallback(() => {
        setDeslocamentoHistorico((valor) => Math.max(0, valor - LIMITE_DO_HISTORICO));
    }, []);
    const irParaProximaPagina = useCallback(() => {
        setDeslocamentoHistorico((valor) => valor + LIMITE_DO_HISTORICO);
    }, []);
    const pedirExclusao = useCallback((item) => {
        if (excluindoId !== null || token === null) {
            return;
        }
        if (item.status === 'RUNNING') {
            return;
        }
        setErroDeExclusao(null);
        setErroDeAtualizacao(null);
        setConfirmacaoDeExclusao(item);
    }, [excluindoId, token]);
    const cancelarConfirmacaoDeExclusao = useCallback(() => {
        if (excluindoId !== null) {
            return;
        }
        setConfirmacaoDeExclusao(null);
        setErroDeExclusao(null);
        setErroDeAtualizacao(null);
    }, [excluindoId]);
    const confirmarExclusao = useCallback(async () => {
        const item = confirmacaoDeExclusao;
        if (item === null || token === null || excluindoId !== null) {
            return;
        }
        setExcluindoId(item.ingestion_id);
        setErroDeExclusao(null);
        setErroDeAtualizacao(null);
        let exclusaoConcluida = false;
        try {
            await excluirIngestao(token, item.ingestion_id);
            exclusaoConcluida = true;
        }
        catch (falha) {
            if (falha instanceof ErroDaGeoEngine) {
                setErroDeExclusao(falha);
            }
            else if (!(falha instanceof DOMException && falha.name === 'AbortError')) {
                setErroDeExclusao('desconhecida');
            }
            setExcluindoId(null);
            return;
        }
        if (item.ingestion_id === ingestionSelecionada) {
            setIngestionSelecionada(null);
        }
        const falhasDeAtualizacao = [];
        try {
            const { dados: historicoAtualizado } = await listarHistoricoDeIngestoes(token, {
                limit: LIMITE_DO_HISTORICO,
                offset: deslocamentoHistorico,
            });
            setHistorico(historicoAtualizado);
            if (historicoAtualizado.itens.length === 0 && deslocamentoHistorico > 0) {
                setDeslocamentoHistorico(Math.max(0, deslocamentoHistorico - LIMITE_DO_HISTORICO));
            }
        }
        catch (falha) {
            if (falha instanceof ErroDaGeoEngine) {
                falhasDeAtualizacao.push({ origem: 'historico', falha });
            }
            else if (!(falha instanceof DOMException && falha.name === 'AbortError')) {
                falhasDeAtualizacao.push({ origem: 'historico', falha: 'desconhecida' });
            }
        }
        try {
            const { dados: camadasAtualizadas } = await listarCamadasAdmin(token);
            setCamadas(camadasAtualizadas);
        }
        catch (falha) {
            if (falha instanceof ErroDaGeoEngine) {
                falhasDeAtualizacao.push({ origem: 'catalogo', falha });
            }
            else if (!(falha instanceof DOMException && falha.name === 'AbortError')) {
                falhasDeAtualizacao.push({ origem: 'catalogo', falha: 'desconhecida' });
            }
        }
        if (falhasDeAtualizacao.length > 0) {
            setErroDeAtualizacao(falhasDeAtualizacao);
        }
        if (exclusaoConcluida) {
            setErroDeExclusao(null);
            setConfirmacaoDeExclusao(null);
        }
        setExcluindoId(null);
    }, [confirmacaoDeExclusao, excluindoId, token, ingestionSelecionada, deslocamentoHistorico]);
    useEffect(() => {
        const controlador = new AbortController();
        void carregarHistorico(controlador.signal);
        void carregarCamadas(controlador.signal);
        return () => controlador.abort();
    }, [carregarHistorico, carregarCamadas]);
    // Acompanhamento da execução selecionada: consulta a cada 3s enquanto RUNNING. Interrompe ao
    // trocar de execução, ao desmontar a página ou quando a sessão administrativa deixa de existir.
    const temporizadorRef = useRef(null);
    useEffect(() => {
        if (ingestionSelecionada === null || token === null) {
            setSituacao(null);
            return;
        }
        let cancelado = false;
        const controlador = new AbortController();
        let consultaEmAndamento = false;
        let pollingEncerrado = false;
        setSituacao(null);
        setErroDeConsulta(null);
        const interromperPolling = () => {
            pollingEncerrado = true;
            controlador.abort();
            if (temporizadorRef.current !== null) {
                clearTimeout(temporizadorRef.current);
                temporizadorRef.current = null;
            }
        };
        const consultar = async () => {
            if (cancelado || pollingEncerrado || consultaEmAndamento) {
                return;
            }
            consultaEmAndamento = true;
            setConsultandoSituacao(true);
            try {
                const { dados } = await consultarSituacaoDaIngestao(token, ingestionSelecionada, controlador.signal);
                if (cancelado || pollingEncerrado) {
                    return;
                }
                setSituacao(dados);
                setErroDeConsulta(null);
                if (dados.status === 'RUNNING') {
                    temporizadorRef.current = setTimeout(() => void consultar(), INTERVALO_DE_POLLING_MS);
                }
                else {
                    void carregarHistorico();
                    void carregarCamadas();
                }
            }
            catch (falha) {
                if (controlador.signal.aborted || cancelado) {
                    return;
                }
                if (falha instanceof ErroDaGeoEngine) {
                    const status = falha.registro.status;
                    setErroDeConsulta(falha);
                    if (status === 401) {
                        interromperPolling();
                        sair();
                        return;
                    }
                    if (status === 403) {
                        interromperPolling();
                        return;
                    }
                    if (status === 404) {
                        interromperPolling();
                        return;
                    }
                    if (status >= 400 && status < 500) {
                        interromperPolling();
                        return;
                    }
                }
                else if (falha instanceof DOMException && falha.name === 'AbortError') {
                    return;
                }
                else {
                    setErroDeConsulta('desconhecida');
                }
                if (!pollingEncerrado) {
                    temporizadorRef.current = setTimeout(() => void consultar(), INTERVALO_DE_POLLING_MS);
                }
            }
            finally {
                consultaEmAndamento = false;
                if (!cancelado) {
                    setConsultandoSituacao(false);
                }
            }
        };
        void consultar();
        return () => {
            cancelado = true;
            interromperPolling();
        };
    }, [ingestionSelecionada, token, carregarHistorico, carregarCamadas, sair]);
    if (token === null) {
        // A rota já redireciona sem sessão válida; este estado é apenas transitório.
        return _jsx("div", { className: "pagina-ingestao", "aria-busy": "true" });
    }
    return (_jsxs("div", { className: "pagina-ingestao", children: [formularioAberto ? (_jsx("main", { className: "pagina-ingestao__corpo", children: _jsx(FormularioDeIngestao, { token: token, aoCancelar: () => setFormularioAberto(false), aoSubmeterComSucesso: (ingestionId) => {
                        setFormularioAberto(false);
                        setIngestionSelecionada(ingestionId);
                        setDeslocamentoHistorico(0);
                        void carregarHistorico();
                    } }) })) : (_jsxs(_Fragment, { children: [_jsx(CabecalhoDePagina, { titulo: t('ingestao.pagina.titulo'), subtitulo: t('ingestao.pagina.subtitulo'), acoes: _jsx("button", { type: "button", className: "botao botao--primario", onClick: () => setFormularioAberto(true), children: t('ingestao.pagina.novaIngestao') }) }), _jsxs("main", { className: "pagina-ingestao__corpo", children: [_jsx(HistoricoDeIngestoes, { itens: historico?.itens ?? null, carregando: carregandoHistorico, erro: erroHistorico, selecionadoId: ingestionSelecionada, limite: historico?.limit ?? null, deslocamento: deslocamentoHistorico, excluindoId: excluindoId, aoSelecionar: setIngestionSelecionada, aoPedirExclusao: pedirExclusao, aoPaginaAnterior: irParaPaginaAnterior, aoProximaPagina: irParaProximaPagina }), _jsx(CatalogoDeCamadas, { itens: camadas, carregando: carregandoCamadas, erro: erroCamadas })] })] })), _jsx(Modal, { aberto: ingestionSelecionada !== null, titulo: t('ingestao.detalhes.titulo'), rotulo: "detalhes-execucao-titulo", onFechar: () => setIngestionSelecionada(null), acoes: _jsx("button", { type: "button", className: "botao botao--secundario", onClick: () => setIngestionSelecionada(null), children: t('ingestao.acao.fechar') }), children: situacao === null ? (_jsx("p", { className: "detalhe-execucao__rodape-info", children: t('ingestao.detalhes.carregando') })) : (_jsxs(_Fragment, { children: [erroDeConsulta !== null ? (_jsx("p", { className: "detalhe-execucao__erro", role: "alert", children: textoDaFalha(erroDeConsulta, t('ingestao.consulta.falhaGenerica')) })) : null, _jsxs("dl", { className: "detalhe-execucao__grade", children: [_jsx("p", { className: "detalhe-execucao__rotulo", children: t('ingestao.detalhes.rotuloStatus') }), _jsxs("p", { className: "detalhe-execucao__valor", children: [_jsx("span", { className: `etiqueta-status etiqueta-status--${situacao.status.toLowerCase()}`, children: rotuloDoStatus(situacao.status, t) }), consultandoSituacao ? (_jsx("span", { className: "detalhe-execucao__rodape-info", children: t('ingestao.detalhes.atualizando') })) : null] }), _jsx("p", { className: "detalhe-execucao__rotulo", children: t('ingestao.detalhes.rotuloInicio') }), _jsx("p", { className: "detalhe-execucao__valor", children: formatar.dataHora(situacao.started_at) }), _jsx("p", { className: "detalhe-execucao__rotulo", children: t('ingestao.detalhes.rotuloTermino') }), _jsx("p", { className: "detalhe-execucao__valor", children: situacao.finished_at === null ? '—' : formatar.dataHora(situacao.finished_at) }), _jsx("p", { className: "detalhe-execucao__rotulo", children: t('ingestao.detalhes.rotuloLidos') }), _jsx("p", { className: "detalhe-execucao__valor", children: contagem(situacao.records_read) }), _jsx("p", { className: "detalhe-execucao__rotulo", children: t('ingestao.detalhes.rotuloValidos') }), _jsx("p", { className: "detalhe-execucao__valor", children: contagem(situacao.records_valid) }), _jsx("p", { className: "detalhe-execucao__rotulo", children: t('ingestao.detalhes.rotuloRejeitados') }), _jsx("p", { className: "detalhe-execucao__valor", children: contagem(situacao.records_rejected) }), _jsx("p", { className: "detalhe-execucao__rotulo", children: t('ingestao.detalhes.rotuloMensagem') }), _jsx("p", { className: "detalhe-execucao__valor", children: situacao.message ?? '—' }), situacao.evidencias.length > 0 ? (_jsxs(_Fragment, { children: [_jsx("p", { className: "detalhe-execucao__rotulo", children: t('ingestao.detalhes.rotuloEvidencias') }), _jsx("ul", { className: "detalhe-execucao__evidencias", children: situacao.evidencias.map((evidencia) => (_jsxs("li", { children: [evidencia.validation_type, " \u00B7 ", evidencia.severity, " \u00B7 ", formatar.inteiro(evidencia.total)] }, `${evidencia.validation_type}-${evidencia.severity}`))) })] })) : null] }), consultandoSituacao ? null : (_jsx("p", { className: "detalhe-execucao__rodape-info", children: t('ingestao.detalhes.acompanhamento') }))] })) }), _jsx(Modal, { aberto: confirmacaoDeExclusao !== null, titulo: t('ingestao.exclusao.titulo'), rotulo: "confirmacao-exclusao-titulo", compacto: true, onFechar: cancelarConfirmacaoDeExclusao, acoes: _jsxs(_Fragment, { children: [_jsx("button", { type: "button", className: "botao botao--secundario", onClick: cancelarConfirmacaoDeExclusao, disabled: excluindoId !== null, children: t('ingestao.acao.cancelar') }), _jsxs("button", { type: "button", className: "botao botao--perigoso-solido", onClick: () => {
                                void confirmarExclusao();
                            }, disabled: excluindoId !== null, children: [_jsx(IconeExcluir, {}), excluindoId !== null ? t('ingestao.exclusao.excluindo') : t('ingestao.exclusao.confirmar')] })] }), children: confirmacaoDeExclusao !== null ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: "confirmacao-exclusao__cabecalho", children: [_jsx("span", { className: "confirmacao-exclusao__alerta", "aria-hidden": "true", children: _jsx(IconeAlerta, {}) }), _jsxs("div", { className: "confirmacao-exclusao__identificador", children: [_jsx("p", { className: "confirmacao-exclusao__identificador-rotulo", children: t('ingestao.exclusao.execucaoSelecionada') }), _jsx("p", { className: "confirmacao-exclusao__identificador-nome", children: confirmacaoDeExclusao.camada_public_id ??
                                                confirmacaoDeExclusao.dataset_nome ??
                                                confirmacaoDeExclusao.ingestion_id })] })] }), _jsx("p", { className: "confirmacao-exclusao__aviso", children: renderizarComDestaque(t('ingestao.exclusao.aviso', { irreversivel: destacar(t('ingestao.exclusao.irreversivel')) })) }), erroDeExclusao !== null ? (_jsx("p", { className: "confirmacao-exclusao__erro", role: "alert", children: textoDaFalha(erroDeExclusao, t('ingestao.exclusao.falhaGenerica')) })) : null, erroDeAtualizacao !== null ? (_jsx("p", { className: "confirmacao-exclusao__atualizacao", role: "status", children: t('ingestao.exclusao.atualizacaoFalhou', {
                                falhas: erroDeAtualizacao
                                    .map(({ origem, falha }) => {
                                    const detalhe = textoDaFalha(falha, t('ingestao.exclusao.falhaDesconhecida'));
                                    return origem === 'historico'
                                        ? t('ingestao.exclusao.falhaHistorico', { detalhe })
                                        : t('ingestao.exclusao.falhaCatalogo', { detalhe });
                                })
                                    .join('; '),
                            }) })) : null] })) : null })] }));
}
